import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef } from 'react'

export default function About() {
  const { t } = useTranslation()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const statsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (inView && statsRef.current) {
      const counters = statsRef.current.querySelectorAll('[data-count]')
      counters.forEach((counter) => {
        const target = parseInt(counter.getAttribute('data-count') || '0')
        const duration = 2000
        const increment = target / (duration / 16)
        let current = 0

        const updateCounter = () => {
          current += increment
          if (current < target) {
            counter.textContent = Math.floor(current) + (counter.textContent?.includes('+') ? '+' : '')
            requestAnimationFrame(updateCounter)
          } else {
            counter.textContent = target + (counter.textContent?.includes('+') ? '+' : '')
          }
        }
        updateCounter()
      })
    }
  }, [inView])

  return (
    <section className="py-24 bg-white dark:bg-slate-900" id="a-propos" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-50 dark:bg-slate-800 rounded-[3rem] p-12 lg:p-20 overflow-hidden relative">
          <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-5xl font-bold mb-8">{t('about.title')}</h2>
              <div className="space-y-6 text-lg text-slate-600 dark:text-slate-400">
                <p>
                  {t('about.p1')}
                </p>
                <p>
                  {t('about.p2')}
                </p>
                <div className="flex items-center gap-6 pt-8" ref={statsRef}>
                  <div className="text-center">
                    <p className="text-primary text-3xl font-bold" data-count="15">0</p>
                    <p className="text-sm font-semibold uppercase">+</p>
                    <p className="text-sm font-semibold uppercase">{t('about.expertise')}</p>
                  </div>
                  <div className="w-[1px] h-12 bg-slate-200 dark:bg-slate-700"></div>
                  <div className="text-center">
                    <p className="text-primary text-3xl font-bold" data-count="100">0</p>
                    <p className="text-sm font-semibold uppercase">%</p>
                    <p className="text-sm font-semibold uppercase">{t('about.independent')}</p>
                  </div>
                  <div className="w-[1px] h-12 bg-slate-200 dark:bg-slate-700"></div>
                  <div className="text-center">
                    <p className="text-primary text-3xl font-bold">24/7</p>
                    <p className="text-sm font-semibold uppercase">{t('about.support')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <motion.img
                whileHover={{ rotate: 0 }}
                initial={{ rotate: 3 }}
                alt="Team meeting"
                className="rounded-3xl shadow-2xl transition-transform duration-500"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuCLDjtA1wz-oI7S7WnsDO0BaW5auorwVDVM87PbcPLI8slgWpaTR4vupfRlk8shqe38jW3uDkyDasEsAwF2aCKPhtODTOOy--TinAAKxy338Ao1wdat-aNqmQPhaZCv6ziOgrTkstNxnkbeuFlFDZkIEZzkuBHjUWvkLxJPJvDqZqcjB2bjiCrOPRE_2usAh7bQaIcFoFPTEl7BmLfRhgB57TFfhtzQzgvVyJb2-rdMrH60Je_iwLOT4tECci7pF0exCzSBUUTjqw"
              />
            </motion.div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32"></div>
        </div>
      </div>
    </section>
  )
}
